// Настройки запроса: какие оси генерации доезжают до бэкенда, а какие режим не
// знает вовсе.
//
// Осей, которых у режима нет, в запросе быть не должно: провайдер-конфиги таких
// режимов хранят NULL, и присланное значение сужает подбор до пустого списка —
// генерация падает с «нет доступных провайдеров». Бар при этом честно держит
// значение прошлого режима, поэтому решение принимается здесь, по режиму, а не
// по содержимому стора.

import type { StartGenerationPayload } from "~/entities/chat";
import type { ModeConfig } from "~/shared/data/chat-models";
import type { ChatSettingsValues } from "~/shared/store/useChatSettingsStore";

/**
 * Настройки для запроса генерации.
 *
 * Режим ещё не резолвился (каталог не загружен) — соотношение и разрешение
 * уходят как есть: это безопасный путь, по нему работали до появления осей.
 *
 * Симметрично квоте цены: там те же оси решают, по какой строке прайса искать
 * стоимость, поэтому расхождение сборщиков означало бы цену на кнопке от одного
 * запроса, а списание — от другого.
 */
export function buildGenerationSettings(
    effective: ChatSettingsValues,
    mode: ModeConfig | undefined,
): StartGenerationPayload["settings"] {
    const aspectApplies = mode ? mode.aspectRatios.length > 0 : true;
    // Разрешение — такая же необязательная ось: у Nano Banana первой версии
    // размер вывода задаёт только соотношение сторон.
    const resolutionApplies = mode ? mode.resolutions.length > 0 : true;

    return {
        ...(aspectApplies ? { aspectRatio: effective.aspectRatio } : {}),
        ...(resolutionApplies ? { resolution: effective.resolution } : {}),
        ...(effective.artifactType === "video" && effective.duration > 0
            ? { durationSeconds: effective.duration, withSound: effective.audioEnabled }
            : {}),
        // Ориентация персонажа — обязательная нативная настройка режимов, где
        // она есть; null означает «у режима её нет», и поле не отправляется.
        ...(effective.characterOrientation ? { characterOrientation: effective.characterOrientation } : {}),
        // Кратность апскейла: у режима апскейла она и есть задание («во сколько
        // раз»), у прочих режимов её нет.
        ...(effective.upscaleFactor ? { upscaleFactor: effective.upscaleFactor } : {}),
        // Сколько результатов просит кнопка. Шлём всегда: цена на кнопке уже
        // посчитана за это число, и без поля бэк сделал бы одну картинку по цене
        // пакета.
        batchSize: effective.batchSize,
    };
}
